// ClientDetailsPanel — the "Client Details" block on Add/Edit Domain.
//
// Two modes:
//   • source="crm"  (Add):  the user types a CLIENT CODE and presses Fetch; the
//     code is looked up live in the external RABS CRM (via our backend proxy
//     POST /clients/crm-lookup) and the returned fields are shown read-only. On
//     save the backend stores that client into our local `clients` table.
//   • source="local" (Edit): if a client IS linked, it's loaded from our own DB
//     (GET /clients/:clientId) and shown read-only — no Fetch button. But if the
//     domain has NO linked client (client_id null), this falls back to the CRM
//     flow above so the user can type a code + Fetch + link one right from Edit.
//
//   value      = clientCode (string)          — also saved on the domain
//   onChange   = (code) => void               — ignored in local mode (read-only)
//   clientId   = linked client's id           — which client to load in local mode
//   source     = "crm" | "local"              — defaults to "crm" (Add)
import { useState, useEffect, useCallback } from "react";
import {
  Box,
  Stack,
  Button,
  Typography,
  TextField,
  InputAdornment,
  CircularProgress,
} from "@mui/material";
import TravelExploreIcon from "@mui/icons-material/TravelExplore";
import api from "../api";

// Fields the RABS CRM actually returns (verified against the live API), mapped
// to fixed labels in display order. Only the keys below exist in the CRM
// response — it does NOT send GSTIN / account number / billing address / notes,
// so those clients-table fields can't be auto-filled here and are omitted.
// `full: true` spans the whole row.
const CLIENT_FIELDS = [
  { label: "Name", keys: ["client_name"] },
  { label: "Company Name", keys: ["client_comp_name"] },
  { label: "Email", keys: ["client_email"] },
  { label: "Phone", keys: ["client_contact"], phone: true },
  { label: "Registration Date", keys: ["client_reg_date"], date: true },
  { label: "Country", keys: ["client_country"] },
  { label: "State", keys: ["client_state"] },
  { label: "Location", keys: ["client_location"] },
  { label: "Sub-location", keys: ["client_sublocation"] },
];

// Normalize a key for matching: lowercase, strip anything non-alphanumeric.
const normKey = (k) => String(k).toLowerCase().replace(/[^a-z0-9]/g, "");

// Render any scalar value as text; stringify objects/arrays defensively.
function displayValue(v) {
  if (v === null || v === undefined) return "";
  if (typeof v === "object") return JSON.stringify(v);
  return String(v);
}

// DD-MM-YYYY from an ISO-ish date; leaves other strings untouched.
function fmtDate(v) {
  const m = String(v || "").match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${m[3]}-${m[2]}-${m[1]}` : displayValue(v);
}

// Build the read-only rows from a local clients-table record (Edit mode). Keys
// match the /clients API shape (rowToClient) — same labels/order as the CRM view.
function localClientRows(c) {
  if (!c) return [];
  const t = (v) => (v === null || v === undefined ? "" : String(v).trim());
  return [
    { label: "Name", text: t(c.name) },
    { label: "Company Name", text: t(c.companyName) },
    { label: "Email", text: t(c.email) },
    { label: "Phone", text: t(c.phone) },
    { label: "Registration Date", text: fmtDate(c.regDate) },
    { label: "Country", text: t(c.country) },
    { label: "State", text: t(c.state) },
    { label: "Location", text: t(c.locationName) },
    { label: "Sub-location", text: t(c.sublocationName) },
  ];
}

export default function ClientDetailsPanel({
  value,
  onChange,
  colors,
  fieldSx,
  labelSx,
  source = "crm",
  clientId = null,
}) {
  const isLocal = source === "local";
  // In edit (local) mode we show read-only DB details ONLY when a client is
  // actually linked. If the domain has no linked client (client_id null), we fall
  // back to the editable CRM-fetch flow so the user can look up + link one here.
  const linkedLocal = isLocal && !!clientId;
  const [details, setDetails] = useState(null); // CRM response (Add mode)
  const [localClient, setLocalClient] = useState(null); // local record (Edit mode)
  const [fetching, setFetching] = useState(false);
  const [error, setError] = useState("");
  const [notFound, setNotFound] = useState(false);

  // CRM lookup — Add mode only (triggered by the Fetch button / Enter).
  const handleFetch = useCallback(async () => {
    const code = String(value || "").trim();
    if (!code || fetching) return;
    setFetching(true);
    setError("");
    setNotFound(false);
    try {
      const res = await api.post("/clients/crm-lookup", { clientCode: code });
      if (res?.found && res.client) {
        setDetails(res.client);
      } else {
        setDetails(null);
        setNotFound(true);
      }
    } catch (err) {
      setError(err.message || "Failed to fetch client details");
    } finally {
      setFetching(false);
    }
  }, [value, fetching]);

  // Edit mode: load the linked client straight from our DB — no CRM, no button.
  useEffect(() => {
    if (!isLocal) return;
    if (!clientId) {
      setLocalClient(null);
      setNotFound(false);
      return;
    }
    let cancelled = false;
    setFetching(true);
    setError("");
    setNotFound(false);
    api
      .get(`/clients/${clientId}`)
      .then((c) => {
        if (cancelled) return;
        if (c) setLocalClient(c);
        else setNotFound(true);
      })
      .catch((err) => {
        if (!cancelled) setError(err.message || "Failed to load client details");
      })
      .finally(() => {
        if (!cancelled) setFetching(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isLocal, clientId]);

  // CRM rows (Add mode) — normalize the CRM response, then pull only our client
  // fields out of it in our fixed order; everything else the CRM sent is ignored.
  const crmRows = (() => {
    if (!details) return [];
    const lookup = {};
    for (const [k, v] of Object.entries(details)) lookup[normKey(k)] = v;
    return CLIENT_FIELDS.map((f) => {
      const hitKey = f.keys.map(normKey).find((nk) => nk in lookup);
      const raw = hitKey ? lookup[hitKey] : "";
      let text = f.date ? fmtDate(raw) : displayValue(raw).trim();
      if (f.phone && text) {
        // Prefix the dial code (CRM key `ccode`, e.g. "91") when present.
        const dial = displayValue(lookup[normKey("ccode")]).trim();
        if (dial) text = `+${dial} ${text}`;
      }
      return { label: f.label, full: !!f.full, text };
    });
  })();

  const rows = linkedLocal ? localClientRows(localClient) : crmRows;

  return (
    <Stack spacing={3}>
      <Box>
        <Typography component="label" sx={labelSx}>
          Client Code
        </Typography>
        <TextField
          value={value || ""}
          onChange={(e) => {
            if (linkedLocal) return; // read-only when a client is linked (edit)
            onChange(e.target.value);
            // Typing a new code invalidates the previously fetched record.
            setDetails(null);
            setNotFound(false);
            setError("");
          }}
          onKeyDown={(e) => {
            if (!linkedLocal && e.key === "Enter") {
              e.preventDefault();
              handleFetch();
            }
          }}
          placeholder="Enter client code"
          fullWidth
          sx={fieldSx}
          slotProps={{
            input: {
              readOnly: linkedLocal,
              endAdornment: linkedLocal ? undefined : (
                <InputAdornment position="end">
                  <Button
                    onClick={handleFetch}
                    disabled={!String(value || "").trim() || fetching}
                    variant="contained"
                    disableElevation
                    startIcon={
                      fetching ? (
                        <CircularProgress size={15} sx={{ color: "#fff" }} />
                      ) : (
                        <TravelExploreIcon sx={{ fontSize: 18 }} />
                      )
                    }
                    sx={{
                      flexShrink: 0,
                      textTransform: "none",
                      fontWeight: 600,
                      fontSize: 13,
                      borderRadius: 1.5,
                      px: 1.75,
                      whiteSpace: "nowrap",
                      color: "#fff",
                      background: "#3B82F6",
                      "&:hover": {
                        background: "#1D4ED8",
                        boxShadow: "0 6px 16px rgba(59, 130, 246, 0.35)",
                      },
                      "&.Mui-disabled": {
                        color: "rgba(255,255,255,0.7)",
                        background: "#BFD3F2",
                      },
                    }}
                  >
                    {fetching ? "Fetching…" : "Fetch"}
                  </Button>
                </InputAdornment>
              ),
            },
          }}
        />
        {error ? (
          <Typography sx={{ fontSize: 12, color: "#DC2626", mt: 1 }}>
            {error}
          </Typography>
        ) : fetching && linkedLocal ? (
          <Typography sx={{ fontSize: 12, color: colors.textFaint, mt: 1 }}>
            Loading client details…
          </Typography>
        ) : notFound ? (
          <Typography sx={{ fontSize: 12, color: "#D97706", mt: 1 }}>
            {linkedLocal
              ? "No client is linked to this domain."
              : "No client found for this code."}
          </Typography>
        ) : linkedLocal ? (
          <Typography sx={{ fontSize: 12, color: colors.textFaint, mt: 1 }}>
            Client details loaded from the database. These fields are read-only.
          </Typography>
        ) : (
          <Typography sx={{ fontSize: 12, color: colors.textFaint, mt: 1 }}>
            Enter the client code and press Fetch to load details from the CRM.
            These fields are read-only.
          </Typography>
        )}
      </Box>

      {rows.length > 0 && (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
            gap: 2.5,
          }}
        >
          {rows.map((row) => (
            <Box
              key={row.label}
              sx={row.full ? { gridColumn: { xs: "1", sm: "1 / -1" } } : undefined}
            >
              <Typography component="label" sx={labelSx}>
                {row.label}
              </Typography>
              <TextField
                fullWidth
                value={row.text}
                placeholder="—"
                multiline={row.full}
                minRows={row.full ? 2 : undefined}
                sx={fieldSx}
                slotProps={{ input: { readOnly: true } }}
              />
            </Box>
          ))}
        </Box>
      )}
    </Stack>
  );
}
